/**
 * What a weather condition puts in the sky — one table for every sky the app
 * draws.
 *
 * `components/controls/WeatherSky.vue` (the weather hero, the modal and the
 * weather page) kept these numbers to itself. The BIM scene's backdrop draws the
 * same sky behind the model (`utils/bimSky.ts`), and a second copy of the table
 * is exactly the "two that nearly match" drift these surfaces exist to avoid: an
 * overcast that greys one sky and not the other reads as a bug in both. So the
 * table lives here, and both skies read it.
 *
 * Pure and dependency-free, so it can be unit-tested.
 */
import { desaturateHex, mixHex } from './solarPosition';

/**
 * What the condition puts in the sky. `dim` is how much the sky darkens under
 * it, `gray` how far its colours (and the clouds') drain toward grey, `cover`
 * how much of the sky it actually seals (0 clear, 1 overcast: how much of the
 * night's stars and moon it hides, and how far an overcast night's grey
 * replaces the near-black of a clear one), and `sunDim` multiplies the sun
 * disc's opacity — an overcast sun is seen *through* cloud, not switched off.
 */
export interface WeatherLook {
  clouds: number;
  cloudOpacity: number;
  precipitation: 'none' | 'drizzle' | 'rain' | 'snow';
  mist: boolean;
  dim: number;
  gray: number;
  cover: number;
  sunDim: number;
}

/**
 * Keyed on the first two characters of the OpenWeatherMap code — the condition
 * group (`01` clear, `09` drizzle, `13` snow …), which is what decides the sky.
 */
const LOOKS: Record<string, WeatherLook> = {
  '01': { clouds: 1, cloudOpacity: 0.55, precipitation: 'none', mist: false, dim: 0, gray: 0, cover: 0, sunDim: 1 },
  '02': {
    clouds: 2,
    cloudOpacity: 0.7,
    precipitation: 'none',
    mist: false,
    dim: 0.04,
    gray: 0.15,
    cover: 0.35,
    sunDim: 0.9,
  },
  '03': {
    clouds: 3,
    cloudOpacity: 0.8,
    precipitation: 'none',
    mist: false,
    dim: 0.1,
    gray: 0.4,
    cover: 0.7,
    sunDim: 0.6,
  },
  '04': {
    clouds: 4,
    cloudOpacity: 0.9,
    precipitation: 'none',
    mist: false,
    dim: 0.2,
    gray: 0.7,
    cover: 0.95,
    sunDim: 0.3,
  },
  '09': {
    clouds: 3,
    cloudOpacity: 0.85,
    precipitation: 'drizzle',
    mist: false,
    dim: 0.22,
    gray: 0.6,
    cover: 0.95,
    sunDim: 0.25,
  },
  '10': {
    clouds: 4,
    cloudOpacity: 0.9,
    precipitation: 'rain',
    mist: false,
    dim: 0.28,
    gray: 0.75,
    cover: 0.98,
    sunDim: 0.18,
  },
  '11': {
    clouds: 4,
    cloudOpacity: 0.95,
    precipitation: 'rain',
    mist: false,
    dim: 0.38,
    gray: 0.8,
    cover: 1,
    sunDim: 0.12,
  },
  '13': {
    clouds: 3,
    cloudOpacity: 0.85,
    precipitation: 'snow',
    mist: false,
    dim: 0.2,
    gray: 0.55,
    cover: 0.9,
    sunDim: 0.3,
  },
  '50': {
    clouds: 2,
    cloudOpacity: 0.4,
    precipitation: 'none',
    mist: true,
    dim: 0.14,
    gray: 0.6,
    cover: 0.85,
    sunDim: 0.45,
  },
};

/**
 * A code this build does not know (a rain shower, a newer group) reads as
 * "partly cloudy" rather than as a clear sky — the safer of the two guesses,
 * and the same default the sky panels have always fallen back to.
 */
export const DEFAULT_LOOK: WeatherLook = {
  clouds: 2,
  cloudOpacity: 0.7,
  precipitation: 'none',
  mist: false,
  dim: 0.05,
  gray: 0.2,
  cover: 0.3,
  sunDim: 0.85,
};

/** The look for an OpenWeatherMap code (`01d`, `04n`, …). */
export function weatherLookFor(condition: string): WeatherLook {
  return LOOKS[condition.slice(0, 2)] ?? DEFAULT_LOOK;
}

/** The clear sky — what a backdrop draws when the hub has no weather to show. */
export const CLEAR_LOOK: WeatherLook = LOOKS['01'] ?? DEFAULT_LOOK;

/**
 * The greys an overcast night is made of. A clear night is near-black, but a
 * covered one never is: the cloud catches whatever light there is and hands it
 * back, which is why a rainy night sky reads grey where a clear one reads black.
 * Mixed in as the cover closes over a dark sky.
 */
export const NIGHT_OVERCAST = { top: '#242c3a', middle: '#2e3644', horizon: '#3c4453' } as const;

/** The three stops of a sky gradient. */
export interface SkyColors {
  top: string;
  middle: string;
  horizon: string;
}

/**
 * The hour's own colours, drained toward grey by however much cloud the
 * condition means — an overcast noon is grey, not a dimmed blue — and lifted to
 * the overcast greys at night, so rain and cloud never paint a black sky.
 *
 * `nightCover` is night × cover: where the overcast grey takes over from the
 * near-black.
 */
export function drainSkyColors(base: SkyColors, look: WeatherLook, nightCover: number): SkyColors {
  const lift = (colour: string, target: string): string => {
    const drained = look.gray > 0 ? desaturateHex(colour, look.gray) : colour;
    return nightCover > 0 ? mixHex(drained, target, nightCover) : drained;
  };
  return {
    top: lift(base.top, NIGHT_OVERCAST.top),
    middle: lift(base.middle, NIGHT_OVERCAST.middle),
    horizon: lift(base.horizon, NIGHT_OVERCAST.horizon),
  };
}

/** The cloud cover's own greys, so white puffs do not betray an overcast sky. */
export function cloudStopsFor(look: WeatherLook): {
  day: { top: string; bottom: string };
  night: { top: string; bottom: string };
} {
  return {
    day: { top: desaturateHex('#ffffff', look.gray), bottom: desaturateHex('#c4d2e6', look.gray) },
    night: { top: desaturateHex('#dbe3f0', look.gray), bottom: desaturateHex('#7d8ca6', look.gray) },
  };
}
